import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Form, Divider, Icon, Button, Message, Feed, Checkbox } from 'semantic-ui-react';
import styled from 'styled-components';
import { Field } from 'formik';
import moment from 'moment';
import _ from 'lodash';

import { Column, SpanCentered } from 'components/Layout';
import { Note } from 'components/Note';

import KironAvatar from 'images/kiron-icon-square.png';

import { DOCUMENTS, ACL, INTERNI_KIRON, TIPOLOGIA_FIRMA_PRIVACY } from 'shared/constants';
import aclService from 'shared/lib/acl';
import VoE from 'shared/lib/ValidOrEmpty';
import AgenziaTecnocasaService from 'shared/services/api/agenziatecnocasa';
import OccupazioneService from 'shared/services/api/anagrafica/occupazione';
import ProfessioneService from 'shared/services/api/anagrafica/professione';
import { StatoCivileService } from 'shared/services/api/listitem';
import ProvenienzaService from 'shared/services/api/provenienza';
import CollaboratoreService from 'shared/services/rpc/gestione-collaboratore';

import ErrorMessage from '../../UI/ErrorMessage';
import { AsyncSelect } from '../../UI/AsyncSelect';
import { Upload } from '../../UI/Upload';
import { Input, CheckBox, CheckboxBtn, Currency } from '../../UI/Input';
import { DatePicker } from '../../UI/DatePicker';
import { SuggestComuneNascita, SuggestIndirizzo, ComuneNascitaCustomField } from '../../UI/GeoSuggest';
import { SingleSelect } from '../../UI/Select';
import { AddableSimple } from '../../UI/AddableSimple';
import { RiconciliabiliList } from './riconciliabiliList';
import DividerForm from '../../UI/DividerForm';
import AddableDebiti from '../../UI/AddableDebiti';
import StoricoPrivacyModal from '../StoricoPrivacyModal';

import { LiveId } from '../LiveId';

const AutoClearField = styled(Form.Field)`
  content: '';
  display: table;
  clear: both;
  display: inline-block;
  position: relative;
`;

const SpanInlineRadio = styled.span`
  text-align: center;
  display: inline-block;
  padding-top: 15px;
`;

const alertCFFields = (event, props, key) => {
  if (_.get(props.values, key)) {
    // eslint-disable-next-line no-unused-expressions, no-alert
    confirm(
      'Il codice fiscale è stato salvato e non può essere modificato.\nSei sicuro di voler modificare questo valore ?',
    )
      ? null
      : event.currentTarget.blur();
  }
};

/*
 * Hack necessario in quanto alcune select ritornano 3 setValue e devo conservare la richiesta del primo alert per non ripeterlo 3 volte
 */
let checkSelect = null;
const alertCFSelect = (name, props, key) => {
  if (name === 'comuneNascita' || name === 'sesso') {
    checkSelect = null;
  }

  if (_.get(props.values, key) && checkSelect === null) {
    // eslint-disable-next-line no-alert
    return confirm(
      'Il codice fiscale è stato salvato e non può essere modificato.\nSei sicuro di voler modificare questo valore ?',
    )
      ? (checkSelect = true)
      : (checkSelect = false);
  }

  return true;
};

export class BaseForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      occupazioni: [],
      professioni: [],
      disabled: this.setDisabled(),
      isStoricoPrivacyOpen: false,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  onCloseStoricoPrivacy = () => {
    this.setState({ isStoricoPrivacyOpen: false });
  };

  setDisabled = () => {
    if (!!this.props.values.videoRiconosciuto && this.props.ruolo === 'Anagrafica') {
      if (
        moment().hour(0).minute(0).second(0).millisecond(0) / 1000 <= this.props.values.scadenzaVideoRiconoscimento &&
        aclService.check(this.props.auth, ACL.CAN_EDIT_FIELDS_VIDEORICONOSCIUTO)
      ) {
        return false;
      }
      if (moment().hour(0).minute(0).second(0).millisecond(0) / 1000 > this.props.values.scadenzaVideoRiconoscimento) {
        return false;
      }
      if (
        this.props.values.liveIdRequests[0].tipologiaRiconoscimento === 'SPID' ||
        this.props.values.liveIdRequests[0].tipologiaRiconoscimento === 'CIE'
      ) {
        return false;
      }
      return true;
    }
    return false;
  };

  loadData = () => {
    OccupazioneService.getOccupazioneOptions().then((res) => {
      this.setState({
        occupazioni: res.options,
      });
    });

    ProfessioneService.getProfessioneOptions().then((res) => {
      this.setState({
        professioni: _.orderBy(res.options, ['label'], ['asc']),
      });
    });
  };
  render() {
    const canEditForzaCF = aclService.check(this.props.auth, ['admin', ...INTERNI_KIRON]);

    return (
      <Form autoComplete="off" onSubmit={this.props.handleSubmit} loading={this.props.isSubmitting}>
        <Grid>
          <Column width={16}>
            {this.props.anagraficheRiconciliabili.length > 0 && (
              <Message negative style={{ marginTop: 22 }}>
                <Message.Header>Anagrafica riconciliabile</Message.Header>
                <p>
                  Sono state trovate delle possibili riconciliazioni con questa anagrafica, controllare la lista in
                  fondo al form
                </p>
              </Message>
            )}
          </Column>
          {this.props.ruolo === 'Anagrafica' && [
            <Column width={16} key="tipologia-anagrafica">
              <Divider horizontal>
                {this.props.isEdit
                  ? this.props.values.formaGiuridica === 'PG'
                    ? 'Impresa'
                    : 'Persona fisica'
                  : 'Tipologia di anagrafica'}
              </Divider>
            </Column>,
            !this.props.isEdit && (
              <Column width={16} textAlign="center" key="forma-giuridica">
                <CheckboxBtn
                  {...this.props}
                  onChange={this.props.setFieldValue}
                  value={this.props.values.formaGiuridica}
                  name="formaGiuridica"
                  elements={[
                    { value: 'PF', label: 'Persona fisica' },
                    { value: 'PG', label: 'Impresa' },
                  ]}
                />
              </Column>
            ),
            <Column width={8} key="collaboratore-assegnato">
              <AsyncSelect
                loadOptions={() =>
                  CollaboratoreService.getCollaboratoreByTipoOption(null, true, null, null, null, true)
                }
                value={this.props.values.collaboratore}
                onChange={this.props.setFieldValue}
                onBlur={this.props.setFieldTouched}
                error={this.props.errors.collaboratore}
                touched={this.props.touched.collaboratore}
                name="collaboratore"
                label="Assegnato a"
                required
              />
            </Column>,
            <Column width={8} key="provenienza-contatto">
              <AsyncSelect
                loadOptions={ProvenienzaService.getOptions}
                value={
                  _.get(this.props.values, 'provenienza.value') ? _.get(this.props.values, 'provenienza.value') : ''
                }
                onChange={(a, b) => {
                  this.props.setFieldValue(a, b);
                  this.props.setFieldValue('dettaglioProvenienza', '');
                  this.props.setFieldValue('provenienzaTecnocasa', '');
                }}
                onBlur={this.props.setFieldTouched}
                error={this.props.errors.provenienza}
                touched={this.props.touched.provenienza}
                name="provenienza"
                label="Provenienza"
                required
              />
            </Column>,
            <Column width={16} key="dettaglio-provenienza">
              <Input
                style={{
                  display:
                    _.get(this.props, 'values.provenienza.provenienzaTecnocasaObbligatoria') ||
                    _.get(this.props.values, 'provenienza.options', []).length > 0
                      ? 'none'
                      : 'block',
                }}
                label="Dettaglio provenienza"
                {...this.props}
                name="dettaglioProvenienza"
                required
              />
              <Grid
                style={{
                  display: _.get(this.props, 'values.provenienza.provenienzaTecnocasaObbligatoria') ? 'block' : 'none',
                  marginTop: 0,
                }}
              >
                <Column width={12}>
                  <AsyncSelect
                    key={`${this.props.values.disableAcl}tecnocasa`}
                    loadOptions={() =>
                      AgenziaTecnocasaService.getAgenzieOptions({ aclEnabled: !this.props.values.disableAcl })
                    }
                    value={this.props.values.provenienzaTecnocasa}
                    onChange={this.props.setFieldValue}
                    onBlur={this.props.setFieldTouched}
                    error={this.props.errors.provenienzaTecnocasa}
                    touched={this.props.touched.provenienzaTecnocasa}
                    disabled={
                      aclService.check(this.props.auth, ACL.CAN_EDIT_PROVENIENZE)
                        ? false
                        : !!_.get(this.props.values, 'provenienza.disabled')
                    }
                    name="provenienzaTecnocasa"
                    label="Dettaglio provenienza Tecnocasa"
                  />
                </Column>
                <Column width={4}>
                  <Checkbox
                    label="Mostra tutte"
                    style={{ marginTop: 32 }}
                    checked={this.props.values.disableAcl}
                    onChange={(e, data) => this.props.setFieldValue('disableAcl', data.checked)}
                  />
                </Column>
              </Grid>
              {_.get(this.props.values, 'provenienza.options', []).length > 0 && (
                <SingleSelect
                  options={this.props.values.provenienza.options}
                  value={this.props.values.dettaglioProvenienza}
                  onChange={(name, value) => this.props.setFieldValue('dettaglioProvenienza', value.value)}
                  onBlur={(name, isTouched) => {
                    this.props.setFieldTouched(name, isTouched);
                  }}
                  error={this.props.errors.dettaglioProvenienza}
                  touched={this.props.touched.dettaglioProvenienza}
                  name="dettaglioProvenienza"
                  label="Dettaglio provenienza"
                  required
                />
              )}
            </Column>,
          ]}
          {this.props.ruolo !== 'Anagrafica' && (
            <div>
              <Column width={16}>
                <Divider horizontal>Dati accesso Login3</Divider>
              </Column>
              <Feed>
                <Feed.Event>
                  <Feed.Label image={KironAvatar} />
                  <Feed.Content>
                    {this.props.ruolo === 'Interno Kiron' || this.props.ruolo === 'Responsabile Area' ? (
                      <Input
                        label="Username"
                        {...this.props}
                        errors={{ codiceL3: this.props.errors.userAcl }}
                        name="codiceL3"
                        required
                      />
                    ) : (
                      <Feed.Date>{_.get(this.props, 'values.userAcl.username')}</Feed.Date>
                    )}
                    <Feed.Summary>
                      {this.props.values && this.props.values.ruoli
                        ? this.props.values.ruoli.map((ruolo) => ruolo.nome).toString()
                        : ''}
                    </Feed.Summary>
                  </Feed.Content>
                </Feed.Event>
              </Feed>
            </div>
          )}
          <Column width={16}>
            <Divider horizontal>Dati anagrafici</Divider>
          </Column>
          {this.props.values.formaGiuridica === 'PF' && [
            <Column width={8} key="nominativo-nome">
              <Input
                label="Nome"
                {...this.props}
                name="nome"
                required
                uppercase
                onClick={(event) => alertCFFields(event, this.props, 'codicefiscale')}
                disabled={this.state.disabled}
              />
            </Column>,
            <Column width={8} key="nominativo-cognome">
              <Input
                label="Cognome"
                {...this.props}
                name="cognome"
                required
                uppercase
                onClick={(event) => alertCFFields(event, this.props, 'codicefiscale')}
                disabled={this.state.disabled}
              />
            </Column>,
            <Column width={8} key="comune-di-nascita">
              <SuggestComuneNascita
                initialValue={this.props.values.comuneNascita ? `${this.props.values.comuneNascita}` : ''}
                onChange={(name, value) =>
                  alertCFSelect(name, this.props) ? this.props.setFieldValue(name, value, 'codicefiscale') : null
                }
                onBlur={(name, isTouched) => {
                  this.props.setFieldTouched(name, isTouched);
                }}
                error={this.props.errors.comuneNascita}
                touched={this.props.touched.comuneNascita}
                name="comuneNascita"
                label="Comune di nascita o stato estero"
                required={this.props.ruolo !== 'Anagrafica' || this.props.validationType === 'cliente'}
                tooltip="In caso di località estera inserire la Nazione di nascita, scegliere sempre città con codice provincia presente"
                disabled={this.state.disabled}
              />
            </Column>,
            <Column width={4} key="data-di-nascita">
              <DatePicker
                {...this.props}
                setFieldTouched={this.props.setFieldTouched}
                touched={this.props.touched}
                required={this.props.ruolo !== 'Anagrafica' || this.props.validationType === 'cliente'}
                value={this.props.values.dataNascita}
                onChange={this.props.setFieldValue}
                name="dataNascita"
                label="Data di nascita"
                startDate={moment().subtract(18, 'years')}
                openDirection="up"
                onClick={(event) => alertCFFields(event, this.props, 'codicefiscale')}
                disabled={this.state.disabled}
              />
            </Column>,
            <Column width={4} key="sesso">
              <SingleSelect
                options={[
                  {
                    value: 'M',
                    label: 'Maschio',
                  },
                  {
                    value: 'F',
                    label: 'Femmina',
                  },
                ]}
                value={this.props.values.sesso}
                onChange={(name, value) =>
                  alertCFSelect(name, this.props) ? this.props.setFieldValue(name, value, 'codicefiscale') : null
                }
                onBlur={(name, isTouched) => {
                  this.props.setFieldTouched(name, isTouched);
                }}
                error={this.props.errors.sesso}
                touched={this.props.touched.sesso}
                name="sesso"
                label="Sesso"
                required={this.props.ruolo !== 'Anagrafica' || this.props.validationType === 'cliente'}
                isDisabled={this.state.disabled}
              />
            </Column>,
          ]}
          <Column width={8} key="codice-fiscale">
            <Input
              label="Codice fiscale"
              {...this.props}
              style={{ position: 'relative' }}
              name="codicefiscale"
              required={this.props.ruolo !== 'Anagrafica' || this.props.validationType === 'cliente'}
              uppercase
              disabled={
                this.props.values.formaGiuridica !== 'PG' &&
                (this.props.values.numeroPratiche > 0 || this.state.disabled)
              }
            />
            {canEditForzaCF && (
              <div style={{ fontSize: '.8em', position: 'absolute', top: 7, right: 15 }}>
                <label style={{ fontWeight: 'bold' }} htmlFor="codicefiscaleForzato">
                  Forza CF
                </label>
                <Field
                  style={{ float: 'left', position: 'relative', marginRight: 3, top: 2 }}
                  type="checkbox"
                  checked={this.props.values.codicefiscaleForzato}
                  name="codicefiscaleForzato"
                />
              </div>
            )}
          </Column>
          <Column width={8} key="partita-iva">
            <Input
              label="Partita IVA"
              {...this.props}
              name="partitaiva"
              required={this.props.values.formaGiuridica === 'PG'}
            />
          </Column>

          {this.props.values.formaGiuridica === 'PF' && [
            <Column width={8} key="stato-civile">
              <AsyncSelect
                loadOptions={StatoCivileService.getAllOptions}
                label="Stato civile"
                name="statoCivile"
                value={this.props.values.statoCivile}
                onChange={this.props.setFieldValue}
                onBlur={this.props.setFieldTouched}
                error={this.props.errors.statoCivile}
                touched={this.props.touched.statoCivile}
              />
            </Column>,
            <Column width={4} key="cittadinanza">
              <SingleSelect
                options={[
                  {
                    value: 'Italiana',
                    label: 'Italiana',
                  },
                  {
                    value: 'Comunitaria',
                    label: 'Comunitaria (UE)',
                  },
                  {
                    value: 'Extracomunitaria',
                    label: 'Extracomunitaria',
                  },
                ]}
                value={this.props.values.cittadinanza}
                onChange={this.props.setFieldValue}
                onBlur={this.props.setFieldTouched}
                error={this.props.errors.cittadinanza}
                touched={this.props.touched.cittadinanza}
                name="cittadinanza"
                label="Cittadinanza"
                isDisabled={this.state.disabled}
              />
            </Column>,
            <Column width={4} key="anni-in-italia">
              <Input
                disabled={!this.props.values.cittadinanza.value || this.props.values.cittadinanza.value === 'Italiana'}
                label="N° anni in italia"
                {...this.props}
                name="anniInItalia"
              />
            </Column>,
            <Column width={16} key="indirizzo-residenza">
              <SuggestIndirizzo
                initialValue={
                  this.props.values.indirizzi
                    ? VoE(
                        _.orderBy(
                          this.props.values.indirizzi.filter((value) => value.tipo === 'residenza'),
                          ['createdAt'],
                          ['asc'],
                        )[0],
                        'indirizzoCompleto',
                      )
                    : ''
                }
                onChange={this.props.setFieldValue}
                onBlur={this.props.setFieldTouched}
                error={this.props.errors.indirizzi}
                touched={this.props.touched.residenza}
                elements={this.props.values.indirizzi}
                label="Residenza"
                name="residenza"
                required={
                  (this.props.ruolo !== 'Anagrafica' && this.props.ruolo !== 'Coordinatore') ||
                  this.props.validationType === 'cliente'
                }
              />
            </Column>,
            <Column
              key="label-domicilio-residenza"
              width={8}
              textAlign="left"
              verticalAlign="middle"
              style={{ marginTop: 10 }}
            >
              <b>Domicilio diverso da residenza</b>
            </Column>,
            <Column key="stesso-domicilio-residenza" width={2} textAlign="right">
              <SpanCentered>
                <CheckBox
                  labelRight
                  {...this.props}
                  label="No"
                  value={'false'}
                  name="domicilioDiversoDaResidenza"
                  type="radio"
                  checked={this.props.values.domicilioDiversoDaResidenza === 'false'}
                />
              </SpanCentered>
            </Column>,
            <Column key="diverso-domicilio-residenza" width={3} textAlign="left" verticalAlign="middle">
              <SpanCentered>
                <CheckBox
                  labelRight
                  {...this.props}
                  label="Si"
                  value={'true'}
                  name="domicilioDiversoDaResidenza"
                  type="radio"
                  checked={this.props.values.domicilioDiversoDaResidenza === 'true'}
                />
              </SpanCentered>
            </Column>,
            <Column
              width={16}
              key="domicilio-diverso-residenza"
              style={{ display: this.props.values.domicilioDiversoDaResidenza === 'true' ? 'block' : 'none' }}
            >
              {this.props.values.domicilioDiversoDaResidenza === 'true' && (
                <SuggestIndirizzo
                  initialValue={
                    this.props.values.indirizzi
                      ? VoE(
                          _.orderBy(
                            this.props.values.indirizzi.filter((value) => value.tipo === 'domicilio'),
                            ['createdAt'],
                            ['asc'],
                          )[0],
                          'indirizzoCompleto',
                        )
                      : ''
                  }
                  onChange={this.props.setFieldValue}
                  onBlur={this.props.setFieldTouched}
                  error={this.props.errors.residenza}
                  touched={this.props.touched.residenza}
                  elements={this.props.values.indirizzi}
                  label="Domicilio"
                  name="domicilio"
                />
              )}
            </Column>,
          ]}

          {this.props.values.formaGiuridica === 'PG' && [
            <Column width={16} key="ragione-sociale">
              <Input label="Ragione Sociale" uppercase {...this.props} name="ragioneSociale" required />
            </Column>,
            <Column width={16} key="sdi">
              <Input label="Codice SDI" {...this.props} name="sdi" />
            </Column>,
            <Column width={16} key="sede-legale">
              <SuggestIndirizzo
                initialValue={
                  this.props.values.indirizzi
                    ? VoE(
                        _.orderBy(
                          this.props.values.indirizzi.filter((value) => value.tipo === 'sedelegale'),
                          ['createdAt'],
                          ['asc'],
                        )[0],
                        'indirizzoCompleto',
                      )
                    : ''
                }
                onChange={this.props.setFieldValue}
                onBlur={this.props.setFieldTouched}
                error={this.props.errors.sedelegale}
                touched={this.props.touched.sedelegale}
                elements={this.props.values.indirizzi}
                label="Sede legale"
                name="sedelegale"
                required
              />
            </Column>,
            <Column width={8} key="citta-registro-imprese">
              <ComuneNascitaCustomField
                initialValue={this.props.values.cittaRegistroImprese ? `${this.props.values.cittaRegistroImprese}` : ''}
                onChange={this.props.setFieldValue}
                onBlur={(name, isTouched) => {
                  this.props.setFieldTouched(name, isTouched);
                }}
                error={this.props.errors.cittaRegistroImprese}
                touched={this.props.touched.cittaRegistroImprese}
                name="cittaRegistroImprese"
                provincia="provinciaRegistroImprese"
                label="Comune registro imprese"
                disabled={this.state.disabled}
              />
            </Column>,
            <Column width={8} key="numero-registro-imprese">
              <Input label="Numero registro imprese" {...this.props} name="numeroRegistroImprese" />
            </Column>,
            <Column width={16}>
              <Divider horizontal style={{ marginBottom: 5 }}>
                Legale rappresentante
              </Divider>
            </Column>,
            <Column width={8} key="nominativo-nome-pg">
              <Input
                label="Nome"
                {...this.props}
                name="nome"
                uppercase
                onClick={(event) => alertCFFields(event, this.props, 'codiceFiscaleLegaleRappresentante')}
                disabled={this.state.disabled}
              />
            </Column>,
            <Column width={8} key="nominativo-cognome-pg">
              <Input
                label="Cognome"
                {...this.props}
                name="cognome"
                uppercase
                onClick={(event) => alertCFFields(event, this.props, 'codiceFiscaleLegaleRappresentante')}
                disabled={this.state.disabled}
              />
            </Column>,
            <Column width={8} key="comune-di-nascita-pg">
              <SuggestComuneNascita
                initialValue={this.props.values.comuneNascita ? `${this.props.values.comuneNascita}` : ''}
                onChange={(name, value) =>
                  alertCFSelect(name, this.props)
                    ? this.props.setFieldValue(name, value, 'codiceFiscaleLegaleRappresentante')
                    : null
                }
                onBlur={(name, isTouched) => {
                  this.props.setFieldTouched(name, isTouched);
                }}
                error={this.props.errors.comuneNascita}
                touched={this.props.touched.comuneNascita}
                name="comuneNascita"
                label="Comune di nascita o stato estero"
                required={this.props.ruolo !== 'Anagrafica' || this.props.validationType === 'cliente'}
                tooltip="In caso di località estera inserire la Nazione di nascita, scegliere sempre città con codice provincia presente"
                disabled={this.state.disabled}
              />
            </Column>,
            <Column width={4} key="data-di-nascita-pg">
              <DatePicker
                {...this.props}
                setFieldTouched={this.props.setFieldTouched}
                touched={this.props.touched}
                required={this.props.ruolo !== 'Anagrafica' || this.props.validationType === 'cliente'}
                value={this.props.values.dataNascita}
                onChange={this.props.setFieldValue}
                name="dataNascita"
                label="Data di nascita"
                startDate={moment().subtract(18, 'years')}
                openDirection="up"
                onClick={(event) => alertCFFields(event, this.props, 'codiceFiscaleLegaleRappresentante')}
                disabled={this.state.disabled}
              />
            </Column>,
            <Column width={4} key="sesso-pg">
              <SingleSelect
                options={[
                  {
                    value: 'M',
                    label: 'Maschio',
                  },
                  {
                    value: 'F',
                    label: 'Femmina',
                  },
                ]}
                value={this.props.values.sesso}
                onChange={(name, value) =>
                  alertCFSelect(name, this.props)
                    ? this.props.setFieldValue(name, value, 'codiceFiscaleLegaleRappresentante')
                    : null
                }
                onBlur={(name, isTouched) => {
                  this.props.setFieldTouched(name, isTouched);
                }}
                error={this.props.errors.sesso}
                touched={this.props.touched.sesso}
                name="sesso"
                label="Sesso"
                required={this.props.ruolo !== 'Anagrafica' || this.props.validationType === 'cliente'}
                isDisabled={this.state.disabled}
              />
            </Column>,
            <Column width={8} key="codiceFiscaleLegaleRappresentante">
              <Input
                label="Codice fiscale"
                {...this.props}
                style={{ position: 'relative' }}
                name="codiceFiscaleLegaleRappresentante"
                required
                uppercase
                disabled={false}
              />
            </Column>,
            <Column width={16} key="indirizzo-legale-rappresentante">
              <SuggestIndirizzo
                initialValue={
                  this.props.values.indirizzi
                    ? VoE(
                        _.orderBy(
                          this.props.values.indirizzi.filter((value) => value.tipo === 'residenzaLegaleRappresentante'),
                          ['createdAt'],
                          ['asc'],
                        )[0],
                        'indirizzoCompleto',
                      )
                    : ''
                }
                onChange={this.props.setFieldValue}
                onBlur={this.props.setFieldTouched}
                error={this.props.errors.residenzaLegaleRappresentante}
                touched={this.props.touched.residenzaLegaleRappresentante}
                elements={this.props.values.indirizzi}
                label="Residenza legale rappresentante"
                name="residenzaLegaleRappresentante"
                required
              />
            </Column>,
            <Column width={16}>
              <Divider horizontal style={{ marginBottom: 5 }}>
                Titolare Effettivo
              </Divider>
            </Column>,
            <Column width={8} key="nominativo-nome-tf">
              <Input
                label="Nome"
                {...this.props}
                name="nomeTitolareEffettivo"
                uppercase
                onClick={(event) => alertCFFields(event, this.props, 'codicefiscaleTitolareEffettivo')}
                disabled={this.state.disabled}
              />
            </Column>,
            <Column width={8} key="nominativo-cognome-tf">
              <Input
                label="Cognome"
                {...this.props}
                name="cognomeTitolareEffettivo"
                uppercase
                onClick={(event) => alertCFFields(event, this.props, 'codicefiscaleTitolareEffettivo')}
                disabled={this.state.disabled}
              />
            </Column>,
            <Column width={8} key="comune-di-nascita-tf">
              <ComuneNascitaCustomField
                initialValue={
                  this.props.values.comuneNascitaTitolareEffettivo
                    ? `${this.props.values.comuneNascitaTitolareEffettivo}`
                    : ''
                }
                onChange={this.props.setFieldValue}
                onBlur={(name, isTouched) => {
                  this.props.setFieldTouched(name, isTouched);
                }}
                error={this.props.errors.comuneNascitaTitolareEffettivo}
                touched={this.props.touched.comuneNascitaTitolareEffettivo}
                name="comuneNascitaTitolareEffettivo"
                provincia="provinciaNascitaTitolareEffettivo"
                label="Comune di nascita o stato estero"
                tooltip="In caso di località estera inserire la Nazione di nascita, scegliere sempre città con codice provincia presente"
                disabled={this.state.disabled}
              />
            </Column>,
            <Column width={4} key="data-di-nascita-tf">
              <DatePicker
                {...this.props}
                setFieldTouched={this.props.setFieldTouched}
                touched={this.props.touched}
                value={this.props.values.dataNascitaTitolareEffettivo}
                onChange={this.props.setFieldValue}
                name="dataNascitaTitolareEffettivo"
                label="Data di nascita"
                startDate={moment().subtract(18, 'years')}
                openDirection="up"
                disabled={this.state.disabled}
              />
            </Column>,
            <Column width={4} key="sesso-tf">
              <SingleSelect
                options={[
                  {
                    value: 'M',
                    label: 'Maschio',
                  },
                  {
                    value: 'F',
                    label: 'Femmina',
                  },
                ]}
                value={this.props.values.sessoTitolareEffettivo}
                onChange={this.props.setFieldValue}
                onBlur={(name, isTouched) => {
                  this.props.setFieldTouched(name, isTouched);
                }}
                error={this.props.errors.sessoTitolareEffettivo}
                touched={this.props.touched.sessoTitolareEffettivo}
                name="sessoTitolareEffettivo"
                label="Sesso"
                isDisabled={this.state.disabled}
              />
            </Column>,
            <Column width={8} key="codicefiscaleTitolareEffettivo">
              <Input
                label="Codice fiscale"
                {...this.props}
                style={{ position: 'relative' }}
                name="codicefiscaleTitolareEffettivo"
                uppercase
                disabled={false}
              />
            </Column>,
            <Column width={16} key="indirizzo-titolare-effettivo">
              <SuggestIndirizzo
                initialValue={
                  this.props.values.indirizzi
                    ? VoE(
                        _.orderBy(
                          this.props.values.indirizzi.filter((value) => value.tipo === 'residenzaTitolareEffettivo'),
                          ['createdAt'],
                          ['asc'],
                        )[0],
                        'indirizzoCompleto',
                      )
                    : ''
                }
                onChange={this.props.setFieldValue}
                onBlur={this.props.setFieldTouched}
                error={this.props.errors.residenzaTitolareEffettivo}
                touched={this.props.touched.residenzaTitolareEffettivo}
                elements={this.props.values.indirizzi}
                label="Residenza titolare effettivo"
                name="residenzaTitolareEffettivo"
              />
            </Column>,
          ]}

          <Column width={16} key="recapiti">
            <AddableSimple
              required={this.props.ruolo !== 'Coordinatore'}
              errors={this.props.errors}
              touched={this.props.touched}
              type="recapiti"
              types={['cellulare', 'telefono', 'fax', 'email', 'pec']}
              onChange={this.props.setFieldValue}
              label="recapiti"
              elements={this.props.values.recapiti}
              disabled={this.state.disabled}
              disableDelete={this.state.disabled}
            />
          </Column>
          {this.props.values.formaGiuridica !== 'PG' && (
            <Column width={16}>
              <Column width={16}>
                <Divider horizontal style={{ marginBottom: 5 }}>
                  Privacy
                </Divider>
              </Column>
              {this.props.errors.privacy && (
                <Grid.Column width={16} style={{ marginTop: -15 }}>
                  <ErrorMessage centered>{this.props.errors.privacy}</ErrorMessage>
                </Grid.Column>
              )}
              <Column width={16} textAlign="left" style={{ height: 40 }}>
                <AutoClearField style={{ marginBottom: -15, marginLeft: 15, marginRight: 20 }}>
                  <Field
                    style={{ float: 'left' }}
                    type="checkbox"
                    checked={this.props.values['privacy.profilazione']}
                    name="privacy.profilazione"
                  />
                  <label htmlFor="privacy" style={{ float: 'left', marginLeft: 7, position: 'relative', top: -3 }}>
                    Consenso profilazione
                  </label>
                </AutoClearField>
                <AutoClearField style={{ marginBottom: -15, marginLeft: 15, marginRight: 20 }}>
                  <Field
                    style={{ float: 'left' }}
                    type="checkbox"
                    checked={this.props.values['privacy.marketing']}
                    name="privacy.marketing"
                  />
                  <label htmlFor="privacy" style={{ float: 'left', marginLeft: 7, position: 'relative', top: -3 }}>
                    Consenso marketing
                  </label>
                </AutoClearField>
              </Column>
              <Column width={16} textAlign="left">
                <AutoClearField style={{ marginBottom: -15, marginLeft: 15 }}>
                  <Field
                    style={{ float: 'left' }}
                    type="checkbox"
                    checked={this.props.values['privacy.consensoInvioEmail']}
                    name="privacy.consensoInvioEmail"
                  />
                  <label htmlFor="privacy" style={{ float: 'left', marginLeft: 7, position: 'relative', top: -3 }}>
                    Consenso fattura elettronica
                  </label>
                </AutoClearField>
              </Column>
              <Column width={8}>
                <Input
                  disabled={!this.props.values['privacy.consensoInvioEmail']}
                  placeholder="Email per invio fattura"
                  {...this.props}
                  name="emailFattura"
                  style={{ marginTop: 5, marginLeft: -15 }}
                />
              </Column>
              <br />
              <Column width={16} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div
                  role="button"
                  tabIndex={0}
                  style={{ fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => this.setState({ isStoricoPrivacyOpen: true })}
                >
                  Vedi storico privacy
                </div>
                <StoricoPrivacyModal
                  isOpen={this.state.isStoricoPrivacyOpen}
                  onClose={this.onCloseStoricoPrivacy}
                  anagraficaId={this.props.values.id}
                />
              </Column>
              <br />
            </Column>
          )}
          {this.props.values.formaGiuridica === 'PG' && (
            <Column width={16}>
              <Divider horizontal>Documenti legale rappresentante</Divider>
            </Column>
          )}
          <Column width={16}>
            <Upload
              touched={this.props.touched}
              errors={this.props.errors}
              name="allegatoIdentita"
              showForm="identita"
              anagraficaId={this.props.values.id}
              endpoint={DOCUMENTS.IDENTITA}
              allegatoIdentitaData={this.props.values.allegatoIdentitaData}
              downloadId={
                this.props.values.allegatoIdentita &&
                this.props.values.allegatoIdentitaData &&
                this.props.values.allegatoIdentitaData.file
                  ? this.props.values.allegatoIdentita
                  : false
              }
              setAllegato={(id) => this.props.setFieldValue('allegatoIdentita', id)}
              title="Documento di identità"
              description="Carta di identità, patente o passaporto"
              disabled={this.state.disabled}
              errorContainerStyle={{ position: 'relative', marginTop: 2 }}
              errorMessageStyle={{ marginTop: 2 }}
              onError={(error, parentState, closeModal) => {
                if (error) {
                  // console.log(error, parentState);
                  // const fileName = _.get(parentState, 'formData[0].name', '');
                  closeModal();
                  // const msg = `Errore validazione file: ${fileName}, solo pdf e jpg sono accettati.`;
                  // alert(msg); // eslint-disable-line
                  // if (this.props.notification) {
                  //   this.props.notification().addNotification({
                  //     message: msg,
                  //     level: 'error'
                  //   });
                  // }
                }
              }}
            />
            <Upload
              touched={this.props.touched}
              errors={this.props.errors}
              name="allegatoCodiceFiscale"
              anagraficaId={this.props.values.id}
              endpoint={DOCUMENTS.CODICE_FISCALE}
              downloadId={this.props.values.allegatoCodiceFiscale || false}
              setAllegato={(id) => this.props.setFieldValue('allegatoCodiceFiscale', id)}
              title="Codice Fiscale"
              description="Copia del codice fiscale"
              disabled={this.state.disabled}
              errorContainerStyle={{ position: 'relative', marginTop: 1 }}
              errorMessageStyle={{ marginTop: 0 }}
            />
            <Upload
              touched={this.props.touched}
              errors={this.props.errors}
              name="allegatoPrivacy"
              endpoint={DOCUMENTS.PRIVACY}
              anagraficaId={this.props.values.id}
              tipoFirma={TIPOLOGIA_FIRMA_PRIVACY.TRADIZIONALE}
              downloadId={this.props.values.allegatoPrivacy || false}
              setAllegato={(id) => this.props.setFieldValue('allegatoPrivacy', id)}
              title="Privacy"
              description="Obbligatorio o rimozione dati automatica in 7 giorni dal caricamento."
              disabled={this.state.disabled}
              forceShowVersions
              errorContainerStyle={{ position: 'relative', marginTop: 1 }}
              errorMessageStyle={{ marginTop: 0 }}
            />
            {this.props.values.formaGiuridica === 'PG' && (
              <Upload
                touched={this.props.touched}
                errors={this.props.errors}
                name="allegatoVisuraCamerale"
                anagraficaId={this.props.values.id}
                endpoint={DOCUMENTS.VISURA_CAMERALE}
                downloadId={this.props.values.allegatoVisuraCamerale || false}
                setAllegato={(id) => this.props.setFieldValue('allegatoVisuraCamerale', id)}
                title="Visura Camerale"
                description="Copia della visura camerale"
                disabled={this.state.disabled}
                errorContainerStyle={{ position: 'relative', marginTop: 1 }}
                errorMessageStyle={{ marginTop: 0 }}
              />
            )}
            {!_.isEmpty(this.props.values.liveIdRequests) &&
              (this.props.values.liveIdRequests[0].tipologiaRiconoscimento === 'SPID' ||
                this.props.values.liveIdRequests[0].tipologiaRiconoscimento === 'CIE') && (
                <Upload
                  touched={this.props.touched}
                  errors={this.props.errors}
                  name="moduloIdentificazione"
                  endpoint={DOCUMENTS.MODULO_IDENTIFICAZIONE}
                  downloadId={this.props.values.moduloIdentificazione || false}
                  setAllegato={(id) => this.props.setFieldValue('moduloIdentificazione', id)}
                  title="Modulo identificazione"
                  description="Documento ottenuto in seguito al riconoscimento con SPID o CIE."
                  disabled={this.state.disabled}
                  showVersions
                  hideUpload
                />
              )}
          </Column>
          {this.props.values.formaGiuridica === 'PG' && (
            <Column width={16}>
              <Divider horizontal>Documenti titolare effettivo</Divider>
            </Column>
          )}
          {this.props.values.formaGiuridica === 'PG' && (
            <Column width={16}>
              <Upload
                touched={this.props.touched}
                errors={this.props.errors}
                name="allegatoIdentitaTitolareEffettivo"
                showForm="identita"
                anagraficaId={this.props.values.id}
                endpoint={DOCUMENTS.IDENTITA_TITOLARE_EFFETTIVO}
                allegatoIdentitaData={this.props.values.allegatoIdentitaTitolareEffettivoData}
                downloadId={
                  this.props.values.allegatoIdentitaTitolareEffettivo &&
                  this.props.values.allegatoIdentitaTitolareEffettivoData &&
                  this.props.values.allegatoIdentitaTitolareEffettivoData.file
                    ? this.props.values.allegatoIdentitaTitolareEffettivo
                    : false
                }
                setAllegato={(id) => this.props.setFieldValue('allegatoIdentitaTitolareEffettivo', id)}
                title="Documento di identità"
                description="Carta di identità, patente o passaporto del titolare effettivo"
                disabled={this.state.disabled}
              />
              <Upload
                touched={this.props.touched}
                errors={this.props.errors}
                name="allegatoCodiceFiscaleTitolareEffettivo"
                anagraficaId={this.props.values.id}
                endpoint={DOCUMENTS.CODICE_FISCALE_TITOLARE_EFFETTIVO}
                downloadId={this.props.values.allegatoCodiceFiscaleTitolareEffettivo || false}
                setAllegato={(id) => this.props.setFieldValue('allegatoCodiceFiscaleTitolareEffettivo', id)}
                title="Codice Fiscale"
                description="Copia del codice fiscale del titolare effettivo"
                disabled={this.state.disabled}
              />
              <Upload
                touched={this.props.touched}
                errors={this.props.errors}
                name="allegatoPrivacyTitolareEffettivo"
                anagraficaId={this.props.values.id}
                endpoint={DOCUMENTS.PRIVACY_TITOLARE_EFFETTIVO}
                downloadId={this.props.values.allegatoPrivacyTitolareEffettivo || false}
                setAllegato={(id) => this.props.setFieldValue('allegatoPrivacyTitolareEffettivo', id)}
                title="Privacy"
                description="Documento privacy del titolare effettivo"
                disabled={this.state.disabled}
              />
            </Column>
          )}
          <Column width={16}>
            <Note anagrafica onChange={this.props.setFieldValue} notes={this.props.values.note} />
          </Column>
          <Column width={16}></Column>

          <Column width={8}>
            <Button
              onClick={this.props.getAnagraficheRiconciliabili}
              type="button"
              loading={this.props.loadingAnagraficheRiconciliabili}
              disabled={this.props.loadingAnagraficheRiconciliabili}
            >
              <h4 key="title">
                <Icon name="id card outline" />
                <Icon name="arrow right" />
                <Icon name="id card" /> Anagrafiche riconciliabili
              </h4>
            </Button>
          </Column>

          {this.props.anagraficheRiconciliabili.length > 0 && [
            <DividerForm key="divider" />,
            <Column width={16} key="reconcileColumn">
              <RiconciliabiliList
                key="list"
                setDestination={this.props.reconciliate}
                anagrafiche={this.props.anagraficheRiconciliabili}
              />
            </Column>,
          ]}

          {/* Dati reddituali */}
          {this.props.ruolo === 'Anagrafica' &&
            !_.isEmpty(this.props.cliente) &&
            this.props.values.formaGiuridica === 'PF' && [
              <Column width={16}>
                <Divider horizontal>Dati reddituali</Divider>
              </Column>,
              <Column width={8}>
                <SingleSelect
                  options={this.state.professioni}
                  value={this.props.values.reddito_professione}
                  onChange={this.props.setFieldValue}
                  onBlur={this.props.setFieldTouched}
                  error={this.props.errors.reddito_professione}
                  touched={this.props.touched.reddito_professione}
                  required={this.props.validationType === 'cliente'}
                  name="reddito_professione"
                  label="Professione"
                />
              </Column>,
              <Column width={8}>
                <SingleSelect
                  options={this.state.occupazioni}
                  value={this.props.values.reddito_occupazione}
                  onChange={this.props.setFieldValue}
                  onBlur={this.props.setFieldTouched}
                  error={this.props.errors.reddito_occupazione}
                  required={this.props.validationType === 'cliente'}
                  name="reddito_occupazione"
                  label="Tipologia contratto"
                />
              </Column>,
              <Column
                width={8}
                style={{
                  display: this.props.values.reddito_occupazione.label !== 'Libero Professionista' ? 'block' : 'none',
                }}
              >
                {this.props.values.reddito_occupazione.label !== 'Libero Professionista' && (
                  <Input label="Nome datore di lavoro" {...this.props} name="reddito_nomeDatorelavoro" />
                )}
              </Column>,
              <Column width={8}>
                {this.props.values.reddito_occupazione.label !== 'Libero Professionista' && (
                  <Input label="P.IVA datore di lavoro" {...this.props} name="reddito_partitaivaDatorelavoro" />
                )}
                {this.props.values.reddito_occupazione.label === 'Libero Professionista' && (
                  <Input label="Partita iva" {...this.props} name="partitaiva" />
                )}
              </Column>,
              <Column width={8}>
                <SingleSelect
                  options={[
                    {
                      value: 'privato',
                      label: 'Privato',
                    },
                    {
                      value: 'pubblico',
                      label: 'Pubblico',
                    },
                    {
                      value: 'non_appl',
                      label: 'Non applicabile',
                    },
                  ]}
                  value={this.props.values.reddito_tipologiaDatorelavoro}
                  onChange={this.props.setFieldValue}
                  onBlur={this.props.setFieldTouched}
                  error={this.props.errors.reddito_tipologiaDatorelavoro}
                  touched={this.props.touched.reddito_tipologiaDatorelavoro}
                  name="reddito_tipologiaDatorelavoro"
                  label="Tipologia datore di lavoro"
                />
              </Column>,
              <Column
                width={8}
                style={{
                  display: this.props.values.reddito_occupazione.label !== 'Libero Professionista' ? 'block' : 'none',
                }}
              >
                {this.props.values.reddito_occupazione.label !== 'Libero Professionista' && (
                  <Input label="PEC datore di lavoro" {...this.props} name="reddito_pecDatorelavoro" />
                )}
              </Column>,
              <Column width={6}>
                <Currency label="Reddito mensile netto" {...this.props} name="reddito_redditoMensileNetto" />
              </Column>,
              <Column width={4}>
                <Input label="N° di mensilità" {...this.props} name="reddito_numeroMensilita" />
              </Column>,
              <Column width={6}>
                <Currency label="Reddito annuo netto" {...this.props} name="reddito_redditoAnnuoNetto" />
              </Column>,
              <Column width={6}>
                <Currency label="TFR maturato" {...this.props} name="reddito_tfrMaturato" />
              </Column>,
              <Column width={5}>
                <DatePicker
                  {...this.props}
                  value={this.props.values.reddito_dataAssunzione}
                  onChange={this.props.setFieldValue}
                  name="reddito_dataAssunzione"
                  label="Data di assunzione"
                  startDate={moment().subtract(18, 'years')}
                  openDirection="up"
                />
              </Column>,
              <Column width={5}>
                <Input label="N° di familiari a carico" {...this.props} name="reddito_numeroFamiliariCarico" />
              </Column>,
              <Grid.Row>
                <Column width={4} textAlign="left" verticalAlign="middle">
                  <b>Debiti in corso</b>
                </Column>
                <Column width={2} textAlign="right">
                  <SpanInlineRadio>
                    <CheckBox
                      labelRight
                      {...this.props}
                      label="No"
                      value={'false'}
                      name="debitiInCorso"
                      type="radio"
                      checked={this.props.cliente.debitiInCorso === 'false'}
                    />
                  </SpanInlineRadio>
                </Column>
                <Column width={3} textAlign="left" verticalAlign="middle">
                  <SpanInlineRadio>
                    <CheckBox
                      labelRight
                      {...this.props}
                      label="Si"
                      value={'true'}
                      name="debitiInCorso"
                      type="radio"
                      checked={this.props.cliente.debitiInCorso === 'true'}
                    />
                  </SpanInlineRadio>
                </Column>
              </Grid.Row>,
              <Column width={16}>
                {this.props.values.debitiInCorso === 'true' && (
                  <AddableDebiti
                    {...this.props}
                    elements={this.props.values.debiti}
                    type="debiti"
                    label="Debiti"
                    onChange={this.props.setFieldValue}
                  />
                )}
              </Column>,
            ]}
          {/* Fine dati reddituali */}

          {this.props.ruolo === 'Anagrafica' &&
            !_.isEmpty(this.props.cliente) && [
              <Column width={16}>
                <Divider horizontal>PRIMO APPUNTAMENTO</Divider>
              </Column>,
              <Column width={16} style={{ marginBottom: '1rem' }}>
                {_.isEmpty(this.props.values.appointment) ? (
                  <div style={{ width: '100%', textAlign: 'center' }}>Nessun appuntamento</div>
                ) : (
                  <div>
                    <p>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', fontStyle: 'italic' }}>Data inizio: </span>
                      {this.props.values.appointment.dataInizio}
                    </p>
                    <p>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', fontStyle: 'italic' }}>Data fine: </span>
                      {this.props.values.appointment.dataFine}
                    </p>
                    <p>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', fontStyle: 'italic' }}>Provenienza: </span>
                      {this.props.values.appointment.provenienza}
                    </p>
                    <p>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', fontStyle: 'italic' }}>
                        Dettaglio provenienza:{' '}
                      </span>
                      {this.props.values.appointment.dettaglioProvenienza}
                    </p>
                  </div>
                )}
              </Column>,
            ]}

          {/* Video riconoscimento */}
          {this.props.ruolo === 'Anagrafica' &&
            _.get(this.props, 'values.liveIdRequests', []).length > 0 && [
              <Column width={16}>
                <Divider horizontal>Riconoscimento remoto </Divider>
              </Column>,
              <Column width={16}>
                <p>
                  <span style={{ fontSize: '16px', fontWeight: 'bold', fontStyle: 'italic' }}> Stato:</span>{' '}
                  {this.props.values.liveIdRequests[0].stateDescription}{' '}
                </p>
                <p>
                  <span style={{ fontSize: '16px', fontWeight: 'bold', fontStyle: 'italic' }}> Data invio:</span>{' '}
                  {this.props.values.liveIdRequests[0].createdAt}{' '}
                </p>
                <p>
                  <span style={{ fontSize: '16px', fontWeight: 'bold', fontStyle: 'italic' }}> Messaggio:</span>{' '}
                  {this.props.values.liveIdRequests[0].skipReason || ' --'}{' '}
                </p>
                <p>
                  <span style={{ fontSize: '16px', fontWeight: 'bold', fontStyle: 'italic' }}> Collaboratore:</span>{' '}
                  {this.props.values.liveIdRequests[0].createdBy}{' '}
                </p>
                {this.props.values.liveIdRequests[0].dataAnnullamento && (
                  <p>
                    <span style={{ fontSize: '16px', fontWeight: 'bold', fontStyle: 'italic' }}>
                      {' '}
                      Data annullamento:
                    </span>{' '}
                    {this.props.values.liveIdRequests[0].dataAnnullamento || ' --'}{' '}
                  </p>
                )}
                {this.props.values.liveIdRequests[0].state === 3 && (
                  <p>
                    <p>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', fontStyle: 'italic' }}>
                        {' '}
                        Data completamento:
                      </span>{' '}
                      {this.props.values.liveIdRequests[0].dataCompletamento || ' --'}{' '}
                    </p>
                    <p>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', fontStyle: 'italic' }}> Data scadenza:</span>{' '}
                      {this.props.values.liveIdRequests[0].dataScadenza || ' --'}{' '}
                    </p>
                  </p>
                )}
              </Column>,
            ]}
          {/* Fine Video riconoscimento */}

          <DividerForm />

          <Column width={this.props.ruolo !== 'Anagrafica' ? 5 : 4}>
            <Button
              onClick={this.props.closeModal}
              type="button"
              icon="delete"
              labelPosition="right"
              content="Chiudi"
            />
          </Column>
          {this.props.ruolo === 'Anagrafica' && (
            <LiveId
              anagrafica={this.props.values.id}
              liveIdRequests={this.props.values.liveIdRequests}
              notification={this.props.notification}
              update={this.props.update}
              loading={this.props.isSubmitting}
              close={this.props.closeModal}
              setSubmitting={this.props.setSubmitting}
            />
          )}
          <Column
            width={this.props.ruolo !== 'Anagrafica' ? 5 : 4}
            textAlign={this.props.ruolo !== 'Anagrafica' ? 'center' : 'right'}
          >
            <Button
              loading={this.props.isSubmitting}
              onClick={() => this.props.onSaveOnly(true)}
              type="button"
              icon="save"
              labelPosition="right"
              positive
              disabled={this.props.isSubmitting}
              content="Salva"
            />
          </Column>
          {this.props.ruolo !== 'Anagrafica' && (
            <Column width={6} textAlign="right">
              <Button
                loading={this.props.isSubmitting}
                type="submit"
                icon="arrow right"
                labelPosition="right"
                positive
                disabled={this.props.isSubmitting}
                content="Salva e prosegui"
              />
            </Column>
          )}
        </Grid>
      </Form>
    );
  }
}

export default BaseForm;

BaseForm.propTypes = {
  anagraficheRiconciliabili: PropTypes.array,
  reconciliate: PropTypes.func,
  getAnagraficheRiconciliabili: PropTypes.func,
  loadingAnagraficheRiconciliabili: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  setFieldValue: PropTypes.func,
  values: PropTypes.object,
  cliente: PropTypes.object,
  touched: PropTypes.object,
  errors: PropTypes.object,
  setFieldTouched: PropTypes.func,
  handleSubmit: PropTypes.func,
  ruolo: PropTypes.string,
  closeModal: PropTypes.func,
  onSaveOnly: PropTypes.func,
  validationType: PropTypes.string,
  auth: PropTypes.object,
  notification: PropTypes.object,
  setSubmitting: PropTypes.func,
  update: PropTypes.func,
  isEdit: PropTypes.bool,
  liveIdRequests: PropTypes.object,
  moduloIdentificazione: PropTypes.string,
};



// WEBPACK FOOTER //
// ./app/components/Forms/Anagrafica/Base/Form.js